import {
  isFieldForCategory,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_BOOLEAN,
} from '../../util/types';
import { allowedFields } from './CustomListingFields';

const meterFields = [
  'length',
  'beam',
  'loa',
  'draught',
  'draughtKeelup',
  'clearance',
  'headroom',
  'lwl',
  'mastHeight',
];
const speedFields = ['cruisingSpeed', 'maxSpeed'];
const kilogramFields = [
  'displacement',
  'displacementLoaded',
  'dryWeight',
  'ballastWeight',
  'keelWeight',
  'curbWeight',
  'payload',
  'trailerWeight',
];
const allowedSpecFields = [
  'saleClass',
  'condition',
  'brand',
  'model',
  'boatType',
  'layoutType',
  'yearBuilt',
  'hullMaterial',
  'passengerCapacity',
  'beam',
  'clearance',
  'propulsion',
  'cruisingSpeed',
  'maxSpeed',
  'engineModel',
  'enginePower',
  'engineHours',
  'fuelType',
  'numberOfEngines',
  'loa',
  'length',
  'draught',
  'draughtKeelup',
  'lwl',
  'displacement',
  'displacementLoaded',
  'dryWeight',
  'ballastWeight',
  'keelWeight',
  'hullType',
  'keelType',
  'rudderType',
  'hin',
  'deckMaterial',
  'superstructureMaterial',
  'helmPosition',
  'steeringType',
  'cockpitType',
  'numberOfHelms',
  'cabins',
  'berths',
  'toilets',
  'showers',
  'headroom',
  'interiorMaterial',
  'rigType',
  'mastMaterial',
  'mastHeight',
  'sailArea',
  'sailConfiguration',
  'fuelTankCount',
  'fuelTankCapacity',
  'waterTankCapacity',
  'wasteTankCapacity',
  'voltage',
  'batteryCount',
  'trailerIncluded',
  'trailerWeight',
  'payload',
  'curbWeight',
  'flag',
  'registrationType',
  'ceCategory',
];

const labelIdByKey = {
  brand: 'SectionSpecifications.label.brand',
  model: 'SectionSpecifications.label.model',
  boatType: 'SectionSpecifications.label.boatType',
  layoutType: 'SectionSpecifications.label.layoutType',
  yearBuilt: 'SectionSpecifications.label.yearBuilt',
  hullMaterial: 'SectionSpecifications.label.hullMaterial',
  passengerCapacity: 'SectionSpecifications.label.passengerCapacity',
  loa: 'SectionSpecifications.label.loa',
  length: 'SectionSpecifications.label.length',
  beam: 'SectionSpecifications.label.beam',
  draught: 'SectionSpecifications.label.draught',
  clearance: 'SectionSpecifications.label.clearance',
  propulsion: 'SectionSpecifications.label.propulsion',
  numberOfEngines: 'SectionSpecifications.label.numberOfEngines',
  cruisingSpeed: 'SectionSpecifications.label.cruisingSpeed',
  maxSpeed: 'SectionSpecifications.label.maxSpeed',
  engineModel: 'SectionSpecifications.label.engineModel',
  enginePower: 'SectionSpecifications.label.enginePower',
  fuelType: 'SectionSpecifications.label.fuelType',
  engineHours: 'SectionSpecifications.label.engineHours',
  lwl: 'SectionSpecifications.label.lwl',
  displacement: 'SectionSpecifications.label.displacement',
  displacementLoaded: 'SectionSpecifications.label.displacementLoaded',
  dryWeight: 'SectionSpecifications.label.dryWeight',
  ballastWeight: 'SectionSpecifications.label.ballastWeight',
  keelWeight: 'SectionSpecifications.label.keelWeight',
  hullType: 'SectionSpecifications.label.hullType',
  keelType: 'SectionSpecifications.label.keelType',
  rudderType: 'SectionSpecifications.label.rudderType',
  hin: 'SectionSpecifications.label.hin',
  deckMaterial: 'SectionSpecifications.label.deckMaterial',
  superstructureMaterial: 'SectionSpecifications.label.superstructureMaterial',
  helmPosition: 'SectionSpecifications.label.helmPosition',
  steeringType: 'SectionSpecifications.label.steeringType',
  cockpitType: 'SectionSpecifications.label.cockpitType',
  numberOfHelms: 'SectionSpecifications.label.numberOfHelms',
  cabins: 'SectionSpecifications.label.cabins',
  berths: 'SectionSpecifications.label.berths',
  toilets: 'SectionSpecifications.label.toilets',
  showers: 'SectionSpecifications.label.showers',
  headroom: 'SectionSpecifications.label.headroom',
  interiorMaterial: 'SectionSpecifications.label.interiorMaterial',
  rigType: 'SectionSpecifications.label.rigType',
  mastMaterial: 'SectionSpecifications.label.mastMaterial',
  mastHeight: 'SectionSpecifications.label.mastHeight',
  sailArea: 'SectionSpecifications.label.sailArea',
  sailConfiguration: 'SectionSpecifications.label.sailConfiguration',
  fuelTankCount: 'SectionSpecifications.label.fuelTankCount',
  fuelTankCapacity: 'SectionSpecifications.label.fuelTankCapacity',
  waterTankCapacity: 'SectionSpecifications.label.waterTankCapacity',
  wasteTankCapacity: 'SectionSpecifications.label.wasteTankCapacity',
  voltage: 'SectionSpecifications.label.voltage',
  batteryCount: 'SectionSpecifications.label.batteryCount',
  trailerIncluded: 'SectionSpecifications.label.trailerIncluded',
  trailerWeight: 'SectionSpecifications.label.trailerWeight',
  payload: 'SectionSpecifications.label.payload',
  curbWeight: 'SectionSpecifications.label.curbWeight',
  flag: 'SectionSpecifications.label.flag',
  registrationType: 'SectionSpecifications.label.registrationType',
  ceCategory: 'SectionSpecifications.label.ceCategory',
};

const coreOrder = [
  'status',
  'brand',
  'model',
  'boatType',
  'layoutType',
  'yearBuilt',
  'condition',
  'hullMaterial',
  'passengerCapacity',
  'loa',
  'beam',
  'draught',
  'clearance',
];

const engineListingKeys = ['propulsion', 'numberOfEngines', 'cruisingSpeed', 'maxSpeed'];

const otherSpecGroups = [
  {
    key: 'dimensionsWeight',
    titleId: 'SectionSpecifications.group.dimensionsWeight',
    fieldKeys: [
      'lwl',
      'displacement',
      'displacementLoaded',
      'dryWeight',
      'ballastWeight',
      'keelWeight',
    ],
  },
  {
    key: 'hullConstruction',
    titleId: 'SectionSpecifications.group.hullConstruction',
    fieldKeys: [
      'hullType',
      'keelType',
      'rudderType',
      'hin',
      'deckMaterial',
      'superstructureMaterial',
    ],
  },
  {
    key: 'layoutSteering',
    titleId: 'SectionSpecifications.group.layoutSteering',
    fieldKeys: ['helmPosition', 'steeringType', 'cockpitType', 'numberOfHelms'],
  },
  {
    key: 'accommodationInterior',
    titleId: 'SectionSpecifications.group.accommodationInterior',
    fieldKeys: ['cabins', 'berths', 'toilets', 'showers', 'headroom', 'interiorMaterial'],
  },
  {
    key: 'sailingRig',
    titleId: 'SectionSpecifications.group.sailingRig',
    fieldKeys: ['rigType', 'mastMaterial', 'mastHeight', 'sailArea', 'sailConfiguration'],
  },
  {
    key: 'tanksCapacities',
    titleId: 'SectionSpecifications.group.tanksCapacities',
    fieldKeys: ['fuelTankCount', 'fuelTankCapacity', 'waterTankCapacity', 'wasteTankCapacity'],
  },
  {
    key: 'electricalSystems',
    titleId: 'SectionSpecifications.group.electricalSystems',
    fieldKeys: ['voltage', 'batteryCount'],
  },
  {
    key: 'trailerTransport',
    titleId: 'SectionSpecifications.group.trailerTransport',
    fieldKeys: ['trailerIncluded', 'trailerWeight', 'payload', 'curbWeight'],
  },
  {
    key: 'administrationRegulation',
    titleId: 'SectionSpecifications.group.administrationRegulation',
    fieldKeys: ['flag', 'registrationType', 'ceCategory'],
  },
];

const normalizeValue = value => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'unknown') return null;
    return trimmed;
  }
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'object') return null;
  return value;
};

const safeFormatMessage = (intl, id) => {
  if (!id) return null;
  const message = intl.formatMessage({ id });
  return message.includes(id) ? null : message;
};

const formatLabel = (intl, key, fallbackLabel) => {
  const labelId = labelIdByKey[key];
  const label = labelId ? safeFormatMessage(intl, labelId) : fallbackLabel;
  return label ? label.replace(/:\s*$/, '') : null;
};

const formatEnumValue = (intl, idBase, value) => {
  const normalized = normalizeValue(value);
  if (!normalized) return null;
  const label = safeFormatMessage(intl, `${idBase}.${normalized}`);
  return label || normalized;
};

const formatUnitValue = (intl, value, unitId) => {
  const normalized = normalizeValue(value);
  if (!normalized) return null;
  const unit = safeFormatMessage(intl, unitId);
  return unit ? `${normalized} ${unit}` : `${normalized}`;
};

const getStatusFromListingState = listingState => {
  if (listingState === 'published') return 'forSale';
  if (listingState === 'closed') return 'unavailable';
  if (listingState === 'draft') return 'draft';
  return null;
};

export const getSpecificationItems = ({
  publicData,
  metadata,
  listingFieldConfigs,
  categoryConfiguration,
  status,
  listingState,
  condition,
  intl,
}) => {
  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration || {};
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);
  const isFieldForSelectedCategories = fieldConfig =>
    isFieldForCategory(currentCategories, fieldConfig);

  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    )?.filter(f => allowedSpecFields.includes(f.key)) || [];

  const specItems = [];

  propsForCustomFields.forEach(field => {
    if (field.schemaType === SCHEMA_TYPE_LONG) {
      const headingId = [...meterFields, ...kilogramFields].includes(field.key)
        ? `${field.heading}.noUnit`
        : field.heading;
      const label = intl.formatMessage({ id: headingId }).replace(/:\s*$/, '');
      const value = meterFields.includes(field.key)
        ? `${field.value} meter`
        : kilogramFields.includes(field.key)
        ? `${field.value} kg`
        : speedFields.includes(field.key)
        ? `${field.value} ${intl.formatMessage({ id: 'ListingFacts.unit.knots' })}`
        : `${field.value}`;
      specItems.push({ key: field.key, label, value });
    } else if (field.schemaType === SCHEMA_TYPE_ENUM && field.option?.label) {
      const label = intl.formatMessage({ id: field.heading }).replace(/:\s*$/, '');
      const value = intl.formatMessage({ id: field.option.label });
      specItems.push({ key: field.key, label, value });
    } else if (field.schemaType === SCHEMA_TYPE_TEXT) {
      const label = intl.formatMessage({ id: field.heading }).replace(/:\s*$/, '');
      const value = `${field.value ?? ''}`.trim();
      specItems.push({ key: field.key, label, value });
    } else if (field.schemaType === SCHEMA_TYPE_BOOLEAN) {
      const label = intl.formatMessage({ id: field.heading }).replace(/:\s*$/, '');
      const boolId = field.value ? 'FieldBoolean.yes' : 'FieldBoolean.no';
      const value = intl.formatMessage({ id: boolId });
      specItems.push({ key: field.key, label, value });
    }
  });

  const filteredItems = specItems.filter(
    i => i.value != null && i.value !== '' && i.value !== 'unknown'
  );

  const byKey = new Map(filteredItems.map(i => [i.key, i]));

  const buildItem = (key, fallbackValue) => {
    const fromMap = byKey.get(key);
    const brandFallbackValue =
      key === 'brand'
        ? normalizeValue(publicData?.manufacturerOriginal ?? publicData?.manufacturer)
        : null;
    const value =
      fallbackValue ??
      (fromMap
        ? fromMap.value
        : normalizeValue(publicData?.[key] ?? metadata?.[key] ?? brandFallbackValue));
    const normalized = normalizeValue(value);
    if (normalized === null || normalized === undefined) {
      return null;
    }
    const label = formatLabel(intl, key, fromMap?.label);
    if (!label) {
      return null;
    }
    const finalValue =
      typeof normalized === 'boolean'
        ? intl.formatMessage({ id: normalized ? 'FieldBoolean.yes' : 'FieldBoolean.no' })
        : normalized;
    return { key, label, value: finalValue };
  };

  const coreItems = [];

  const resolvedStatus = normalizeValue(status) || getStatusFromListingState(listingState);
  if (resolvedStatus) {
    const statusLabel = intl
      .formatMessage({ id: 'SectionSpecifications.label.status' })
      .replace(/:\s*$/, '');
    const statusValue = formatEnumValue(intl, 'General.listing.status', resolvedStatus);
    if (statusValue) {
      coreItems.push({ key: 'status', label: statusLabel, value: statusValue });
    }
  }

  const lengthKey =
    byKey.has('loa') || publicData?.loa
      ? 'loa'
      : byKey.has('length') || publicData?.length
      ? 'length'
      : null;

  coreOrder.forEach(key => {
    if (key === 'status') return;
    if (key === 'condition') {
      if (!condition) return;
      const conditionLabel = intl
        .formatMessage({ id: 'EditListingLocationForm.conditionLabel' })
        .replace(/:\s*$/, '');
      const conditionValue = formatEnumValue(intl, 'General.listing.condition', condition);
      if (conditionValue) {
        coreItems.push({ key: 'condition', label: conditionLabel, value: conditionValue });
      }
      return;
    }
    if (key === 'loa') {
      if (!lengthKey) return;
      const lengthItem = buildItem(lengthKey);
      if (lengthItem) coreItems.push(lengthItem);
      return;
    }
    const item = buildItem(key);
    if (item) coreItems.push(item);
  });

  const engineListingItems = engineListingKeys.map(key => buildItem(key)).filter(Boolean);

  const engines = Array.isArray(publicData?.boatEngines) ? publicData.boatEngines : [];
  const engineCount = engines.length;
  const engineSubgroups = engines.slice(0, 2).map((engine, index) => {
    const items = [];
    const summaryParts = [];
    const manufacturerValue = normalizeValue(engine?.engineManufacturer);
    if (manufacturerValue) summaryParts.push(manufacturerValue);
    const modelValue = normalizeValue(engine?.engineModel);
    if (modelValue) {
      const item = buildItem('engineModel', modelValue);
      if (item) items.push(item);
      summaryParts.push(modelValue);
    }
    const fuelValue = formatEnumValue(intl, 'General.listing.fuelType', engine?.fuelType);
    if (fuelValue) {
      const item = buildItem('fuelType', fuelValue);
      if (item) items.push(item);
      summaryParts.push(fuelValue);
    }
    const powerValue = formatUnitValue(intl, engine?.enginePower, 'ListingFacts.unit.pk');
    if (powerValue) {
      const item = buildItem('enginePower', powerValue);
      if (item) items.push(item);
      summaryParts.push(powerValue);
    }
    const hoursValue = formatUnitValue(intl, engine?.engineHours, 'ListingFacts.unit.hours');
    if (hoursValue) {
      const item = buildItem('engineHours', hoursValue);
      if (item) items.push(item);
      summaryParts.push(hoursValue);
    }
    if (!items.length && !summaryParts.length) return null;
    return {
      key: `engine-${index + 1}`,
      titleId:
        engineCount > 1
          ? index === 0
            ? 'SectionSpecifications.group.engine1'
            : 'SectionSpecifications.group.engine2'
          : 'SectionSpecifications.group.engineSingle',
      items,
      summary: summaryParts.length ? summaryParts.join(' • ') : null,
    };
  });

  if (!engineSubgroups.filter(Boolean).length) {
    const fallbackItems = ['engineModel', 'enginePower', 'fuelType', 'engineHours']
      .map(key => buildItem(key))
      .filter(Boolean);
    if (fallbackItems.length) {
      const fallbackMap = new Map(fallbackItems.map(item => [item.key, item.value]));
      const fallbackSummaryParts = [
        fallbackMap.get('engineModel'),
        fallbackMap.get('fuelType'),
        fallbackMap.get('enginePower'),
        fallbackMap.get('engineHours'),
      ].filter(Boolean);
      engineSubgroups.push({
        key: 'engine-1',
        titleId:
          engineCount > 1
            ? 'SectionSpecifications.group.engine1'
            : 'SectionSpecifications.group.engineSingle',
        items: fallbackItems,
        summary: fallbackSummaryParts.join(' • '),
      });
    }
  }

  const cleanedEngineSubgroups = engineSubgroups.filter(Boolean);

  const otherGroups = otherSpecGroups
    .map(group => {
      const items = group.fieldKeys.map(key => buildItem(key)).filter(Boolean);
      return items.length ? { ...group, items } : null;
    })
    .filter(Boolean);

  const hasEngines = engineListingItems.length || cleanedEngineSubgroups.length;
  const hasOther = otherGroups.length;

  const engineItemsFlat = [
    ...engineListingItems,
    ...cleanedEngineSubgroups.flatMap(group => group.items),
  ];
  const otherItemsFlat = otherGroups.flatMap(group => group.items);
  const orderedItems = [...coreItems, ...engineItemsFlat, ...otherItemsFlat];

  return {
    coreItems,
    engineGroup: hasEngines
      ? {
          key: 'engines',
          titleId: 'SectionSpecifications.group.engines',
          listingItems: engineListingItems,
          subgroups: cleanedEngineSubgroups,
        }
      : null,
    otherGroup: hasOther
      ? {
          key: 'other',
          titleId: 'SectionSpecifications.group.other',
          subgroups: otherGroups,
        }
      : null,
    orderedItems,
    groupedItems: [],
    specItems: filteredItems,
  };
};

export const getEquipmentFields = ({
  publicData,
  metadata,
  listingFieldConfigs,
  categoryConfiguration,
}) => {
  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration || {};
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig =>
    isFieldForCategory(currentCategories, fieldConfig);

  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ).filter(f => allowedFields.includes(f.key)) || [];

  return propsForCustomFields
    .filter(f => f.schemaType === SCHEMA_TYPE_MULTI_ENUM)
    ?.map(field => ({ ...field, dontRender: false }));
};

export const getEquipmentSummary = inputs => {
  const equipmentFields = getEquipmentFields(inputs);
  const totalAmenities =
    equipmentFields
      ?.map(field => field?.selectedOptions?.length || 0)
      ?.reduce((acc, val) => acc + val, 0) || 0;
  const hasEquipment = totalAmenities > 0;
  return { equipmentFields, totalAmenities, hasEquipment };
};

const equipmentGroups = [
  {
    key: 'navigation',
    titleId: 'SectionEquipment.group.navigation',
    fieldKeys: ['onBoardInstruments'],
  },
  {
    key: 'comfort',
    titleId: 'SectionEquipment.group.comfort',
    fieldKeys: ['galley', 'equipment'],
  },
  {
    key: 'deck',
    titleId: 'SectionEquipment.group.deck',
    fieldKeys: ['coversAndTarpaulins'],
  },
  {
    key: 'tech',
    titleId: 'SectionEquipment.group.tech',
    fieldKeys: ['technicalEquipment'],
  },
];

export const buildEquipmentItemsByGroup = (intl, equipmentFields) => {
  if (!equipmentFields) {
    return [];
  }
  return equipmentGroups.map(group => {
    const groupFields = equipmentFields.filter(f => group.fieldKeys.includes(f.key));
    const items = groupFields.flatMap(field => {
      const selected = field.selectedOptions || [];
      return selected
        .map(optKey => field.options?.find(o => o.key === optKey))
        .filter(Boolean)
        .map(opt => intl.formatMessage({ id: opt.label }));
    });
    return { ...group, items };
  });
};
